import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Select,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Td,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FaInbox, FaUserTie } from "react-icons/fa";
import { useTranslation } from "react-i18next";

import {
  listIncoming,
  listReceivedConsultantProposals,
} from "../../../api/partnersApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import {
  PARTNERS_INCOMING_STATUSES,
  PARTNERS_INCOMING_STATUS_LABELS,
  PARTNERS_INCOMING_STATUS_COLORS,
  PARTNERS_MISSION_STATUS_LABELS,
  PARTNERS_MISSION_STATUS_LABEL_KEYS,
  PARTNERS_MISSION_STATUS_COLORS,
  PROPOSAL_STATUS_COLORS,
  getStatusLabel,
} from "../../../constants/statuses";
import {
  PageHeader,
  Panel,
  DataTable,
  StatusBadge,
  FilterBar,
  EmptyState,
  TablePagination,
} from "../../../components/Portal/ui";
import PartnersIncomingDetail from "./PartnersIncomingDetail";
import ConsultantProposalDetailModal from "./ConsultantProposalDetailModal";
import PartnersMonthlyChart from "../../../components/Portal/Partners/PartnersMonthlyChart";

const AUTO_REFRESH_INTERVAL = 30000;

// Shared sx for the compact filter-row selects (matches FilterSearch's 31px).
const FILTER_SELECT_SX = {
  height: "31px !important",
  paddingTop: "0 !important",
  paddingBottom: "0 !important",
  fontWeight: 600,
};

const PartnersIncoming = () => {
  const toast = useToast();
  const { t } = useTranslation("portal");
  const { selectedBusinessUnitId, isLoaded: isBUContextLoaded } = useBusinessUnit();

  // Active sub-tab (0 = missions, 1 = consultants) — drives the chart scope.
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // Missions tab state
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const detailModal = useDisclosure();
  const [selectedIncomingId, setSelectedIncomingId] = useState(null);

  // Consultants (proposals) tab state
  const [proposals, setProposals] = useState([]);
  const [proposalsLoading, setProposalsLoading] = useState(false);
  const [proposalsPage, setProposalsPage] = useState(1);
  const [proposalsPageSize, setProposalsPageSize] = useState(10);
  const [proposalsTotalPages, setProposalsTotalPages] = useState(1);
  const [proposalsTotalItems, setProposalsTotalItems] = useState(0);
  const [proposalsStatusFilter, setProposalsStatusFilter] = useState("");
  const [selectedProposal, setSelectedProposal] = useState(null);
  const proposalDetailModal = useDisclosure();

  const refreshTimerRef = useRef(null);

  const fetchIncoming = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const res = await listIncoming(
          { page, pageSize, status: statusFilter || undefined },
          { buId: selectedBusinessUnitId || undefined }
        );
        setItems(res.items || []);
        setTotalItems(res.totalItems || 0);
        setTotalPages(
          res.totalPages || Math.max(1, Math.ceil((res.totalItems || 0) / pageSize))
        );
      } catch (e) {
        if (!silent) {
          toast({
            title: t(
              "partners.receivedRequests.fetchError",
              "Failed to load received requests"
            ),
            status: "error",
            duration: 3000,
          });
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [page, pageSize, statusFilter, selectedBusinessUnitId, toast, t]
  );

  const fetchProposals = useCallback(
    async (silent = false) => {
      if (!silent) setProposalsLoading(true);
      try {
        const res = await listReceivedConsultantProposals(
          {
            page: proposalsPage,
            pageSize: proposalsPageSize,
            status: proposalsStatusFilter || undefined,
          },
          { buId: selectedBusinessUnitId || undefined }
        );
        setProposals(res.items || []);
        setProposalsTotalItems(res.totalItems || 0);
        setProposalsTotalPages(res.totalPages || 1);
      } catch {
        if (!silent) {
          toast({
            title: t(
              "partners.receivedRequests.fetchProposalsError",
              "Failed to load consultant proposals"
            ),
            status: "error",
          });
        }
      } finally {
        if (!silent) setProposalsLoading(false);
      }
    },
    [
      proposalsPage,
      proposalsPageSize,
      proposalsStatusFilter,
      selectedBusinessUnitId,
      toast,
      t,
    ]
  );

  useEffect(() => {
    if (isBUContextLoaded) fetchIncoming();
  }, [fetchIncoming, isBUContextLoaded]);

  useEffect(() => {
    if (isBUContextLoaded) fetchProposals();
  }, [fetchProposals, isBUContextLoaded]);

  useEffect(() => {
    if (!isBUContextLoaded) return;
    refreshTimerRef.current = setInterval(() => {
      fetchIncoming(true);
      fetchProposals(true);
    }, AUTO_REFRESH_INTERVAL);
    return () => clearInterval(refreshTimerRef.current);
  }, [fetchIncoming, fetchProposals, isBUContextLoaded]);

  const handleIncomingRowClick = (incomingId) => {
    setSelectedIncomingId(incomingId);
    detailModal.onOpen();
  };

  const proposalStatusLabel = (status) =>
    t(`partners.consultantProposals.statusLabels.${status}`, status || "-");

  const purposeLabel = (purpose) => {
    const map = {
      contact_pool: t("partners.missions.purposeLabels.contactPool", "Contact Pool"),
      propose_assignment: t(
        "partners.missions.purposeLabels.proposeAssignment",
        "Assignment"
      ),
    };
    return map[purpose] || purpose || "-";
  };

  const incomingColumns = [
    { key: "mission", label: t("partners.incoming.missionTitle", "Mission"), width: "22%" },
    { key: "sender", label: t("partners.incoming.sender", "From"), width: "20%" },
    { key: "purpose", label: t("partners.incoming.purpose", "Purpose"), width: "14%" },
    { key: "missionStatus", label: t("partners.incoming.missionStatus", "Mission Status"), width: "13%" },
    { key: "status", label: t("common.status", "Status"), width: "12%" },
    { key: "submissions", label: t("partners.incoming.submissionsCount", "Submissions"), width: "9%", align: "right" },
    { key: "created", label: t("common.createdAt", "Received"), width: "10%" },
  ];

  const proposalColumns = [
    { key: "from", label: t("partners.consultantProposals.table.from", "From"), width: "22%" },
    { key: "role", label: t("partners.consultantProposals.table.role", "Role") },
    { key: "dailyRate", label: t("partners.consultantProposals.table.dailyRate", "Daily rate"), width: "16%", align: "right" },
    { key: "availability", label: t("partners.consultantProposals.table.availability", "Available"), width: "15%" },
    { key: "status", label: t("partners.consultantProposals.table.status", "Status"), width: "14%" },
    { key: "created", label: t("partners.consultantProposals.table.created", "Received"), width: "14%" },
  ];

  return (
    // Bleed past PortalLayout's p={6} so the page-head spans the content edges.
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%">
      <PageHeader
        moduleCode="AP"
        eyebrow={t("partners.module.eyebrow", "ASAP Partners")}
        title={t("partners.receivedRequests.title", "Received Requests")}
      />

      <Box p="20px">
        <Box mb="12px">
          <PartnersMonthlyChart
            scope={
              activeTabIndex === 1 ? "received_consultants" : "received_missions"
            }
            buId={selectedBusinessUnitId || undefined}
          />
        </Box>

        <Tabs
          variant="line"
          colorScheme="brand"
          index={activeTabIndex}
          onChange={(idx) => setActiveTabIndex(idx)}
        >
          <TabList mb="12px" borderColor="portal.line">
            <Tab fontWeight="800" fontSize="13px">
              {t("partners.receivedRequests.tabs.missions", "Missions")}
            </Tab>
            <Tab fontWeight="800" fontSize="13px">
              {t("partners.receivedRequests.tabs.consultants", "Consultants")}
            </Tab>
          </TabList>

          <TabPanels>
            {/* ─── Missions Tab ─── */}
            <TabPanel p={0}>
              <Panel
                title={t("partners.receivedRequests.tabs.missions", "Missions")}
                count={t("partners.incoming.summary.total", {
                  count: totalItems,
                  defaultValue: "{{count}} request(s)",
                })}
                filters={
                  <FilterBar>
                    <Box flex="1" minW="180px" />
                    <Select
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPage(1);
                      }}
                      w={{ base: "100%", sm: "180px" }}
                      flex="0 0 auto"
                      placeholder={t("common.allStatuses", "All statuses")}
                      sx={FILTER_SELECT_SX}
                    >
                      {PARTNERS_INCOMING_STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>
                          {getStatusLabel(
                            t,
                            undefined,
                            PARTNERS_INCOMING_STATUS_LABELS,
                            s.value
                          ) || s.label}
                        </option>
                      ))}
                    </Select>
                  </FilterBar>
                }
                footer={
                  <TablePagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={(sz) => {
                      setPageSize(sz);
                      setPage(1);
                    }}
                  />
                }
              >
                <DataTable
                  columns={incomingColumns}
                  rows={items}
                  loading={loading}
                  emptyState={
                    <EmptyState
                      icon={<FaInbox />}
                      title={t("partners.incoming.empty", "No incoming missions yet.")}
                      hint={t(
                        "partners.incoming.emptyHint",
                        "Requests sent to you by partner companies will appear here."
                      )}
                    />
                  }
                  renderRow={(item) => (
                    <Tr
                      key={item.id}
                      cursor="pointer"
                      onClick={() => handleIncomingRowClick(item.id)}
                    >
                      <Td fontWeight="700">{item.missionTitle || "-"}</Td>
                      <Td>
                        {item.senderCompanyName || `Company #${item.senderCompanyId}`}
                        {item.senderBuName ? ` — ${item.senderBuName}` : ""}
                      </Td>
                      <Td color="portal.muted">{purposeLabel(item.missionPurpose)}</Td>
                      <Td>
                        <StatusBadge
                          label={
                            getStatusLabel(
                              t,
                              PARTNERS_MISSION_STATUS_LABEL_KEYS,
                              PARTNERS_MISSION_STATUS_LABELS,
                              item.missionStatus
                            ) || "-"
                          }
                          colorScheme={
                            PARTNERS_MISSION_STATUS_COLORS[item.missionStatus] || "gray"
                          }
                        />
                      </Td>
                      <Td>
                        <StatusBadge
                          label={
                            PARTNERS_INCOMING_STATUS_LABELS[item.status] || item.status
                          }
                          colorScheme={
                            PARTNERS_INCOMING_STATUS_COLORS[item.status] || "gray"
                          }
                        />
                      </Td>
                      <Td textAlign="right">{item.submissionCount || 0}</Td>
                      <Td>
                        {item.createdAt
                          ? new Date(item.createdAt).toLocaleDateString()
                          : "-"}
                      </Td>
                    </Tr>
                  )}
                />
              </Panel>
            </TabPanel>

            {/* ─── Consultants Tab ─── */}
            <TabPanel p={0}>
              <Panel
                title={t("partners.receivedRequests.tabs.consultants", "Consultants")}
                count={t("partners.consultantProposals.summary.totalReceived", {
                  count: proposalsTotalItems,
                  defaultValue: "{{count}} proposal(s)",
                })}
                filters={
                  <FilterBar>
                    <Box flex="1" minW="180px" />
                    <Select
                      value={proposalsStatusFilter}
                      onChange={(e) => {
                        setProposalsStatusFilter(e.target.value);
                        setProposalsPage(1);
                      }}
                      w={{ base: "100%", sm: "180px" }}
                      flex="0 0 auto"
                      placeholder={t("common.allStatuses", "All statuses")}
                      sx={FILTER_SELECT_SX}
                    >
                      <option value="pending">{proposalStatusLabel("pending")}</option>
                      <option value="accepted">{proposalStatusLabel("accepted")}</option>
                      <option value="declined">{proposalStatusLabel("declined")}</option>
                    </Select>
                  </FilterBar>
                }
                footer={
                  <TablePagination
                    page={proposalsPage}
                    pageSize={proposalsPageSize}
                    totalItems={proposalsTotalItems}
                    totalPages={proposalsTotalPages}
                    onPageChange={setProposalsPage}
                    onPageSizeChange={(sz) => {
                      setProposalsPageSize(sz);
                      setProposalsPage(1);
                    }}
                  />
                }
              >
                <DataTable
                  columns={proposalColumns}
                  rows={proposals}
                  loading={proposalsLoading}
                  emptyState={
                    <EmptyState
                      icon={<FaUserTie />}
                      title={t(
                        "partners.consultantProposals.emptyReceived",
                        "No consultant proposals received yet."
                      )}
                      hint={t(
                        "partners.consultantProposals.emptyReceivedHint",
                        "Consultants proposed to you by partners will appear here."
                      )}
                    />
                  }
                  renderRow={(p) => (
                    <Tr
                      key={p.id}
                      cursor="pointer"
                      onClick={() => {
                        setSelectedProposal(p);
                        proposalDetailModal.onOpen();
                      }}
                    >
                      <Td fontWeight="700">
                        {p.senderCompanyName || `#${p.senderCompanyId}`}
                      </Td>
                      <Td>{p.title || "—"}</Td>
                      <Td textAlign="right">
                        {p.dailyRate ? `${p.dailyRate} ${p.currency || "EUR"}` : "—"}
                      </Td>
                      <Td>
                        {p.availabilityDate
                          ? new Date(p.availabilityDate).toLocaleDateString()
                          : "—"}
                      </Td>
                      <Td>
                        <StatusBadge
                          label={proposalStatusLabel(p.status)}
                          colorScheme={PROPOSAL_STATUS_COLORS[p.status] || "gray"}
                        />
                      </Td>
                      <Td>
                        {p.createdAt
                          ? new Date(p.createdAt).toLocaleDateString()
                          : "-"}
                      </Td>
                    </Tr>
                  )}
                />
              </Panel>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      <PartnersIncomingDetail
        isOpen={detailModal.isOpen}
        onClose={detailModal.onClose}
        incomingId={selectedIncomingId}
        onStatusChange={fetchIncoming}
      />

      <ConsultantProposalDetailModal
        isOpen={proposalDetailModal.isOpen}
        onClose={() => {
          proposalDetailModal.onClose();
          setSelectedProposal(null);
        }}
        proposal={selectedProposal}
        isReceiver={true}
        onChanged={() => fetchProposals()}
      />
    </Box>
  );
};

export default PartnersIncoming;
